import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk, RootState } from "@store/index";
import { getErrorMessageFromResponse } from "@helpers/get-error-message";

import {
  BillLadingCommonProps,
  BillLadingCreate,
  CommoditiesProps,
  CommodityWithSharedUI,
  CreateCommoditiesFields,
  CreateVoyage,
  MaritimeEquipment,
  VoyageInfo,
} from "./types";
import { ShippingCaseResponse } from "@pages/shippingView/types";
import { VoyageInitialState } from "./config";
import {
  createBillLadingApi,
  createCommodityApi,
  createContainerApi,
  createVoyageApi,
  exportCargoDetailsApi,
  fetchBillLadingApi,
  fetchBillLadingItemApi,
  fetchPackingListApi,
  getCommoditiesApi,
  getContainerByIdApi,
  getContainersApi,
  getVoyageApi,
  loadFileApi,
  removeBillApi,
  removeCommodityApi,
  removeContainerApi,
  updateBillLadingApi,
  updateCommodityApi,
  updateContainerApi,
  updateVoyageApi,
} from "./api";
import { getSharedColorByCommodityId } from "@helpers/id-hsl-convertor";
import { PackingProps } from "../unloading/types";

type ViewState<T> = {
  items: T;
  loading: boolean;
  error: string | null;
  success: string | null;
};

type CargoState = {
  container: {
    success: string | undefined;
    error: string | undefined;
  };
  tempContainer: {
    item: MaritimeEquipment | null;
    success: string | undefined;
    error: string | undefined;
  };
  uploading: {
    loading: boolean;
    error: string | null;
    success: string | null;
  };
  voyage: ViewState<VoyageInfo[] | null>;
  billOfLading: ViewState<BillLadingCommonProps[] | null>;
  packing_list: {
    entity: string | null;
    entity_id: number | null;
    group_id: number | null;
    items: PackingProps[] | null;
    loading: boolean;
    error: string | null;
    success: string | null;
    total: number;
  };
  exporting: {
    loading: boolean;
    error: string | null;
  };
};

export type ViewStateKey = "voyage" | "billOfLading" | "packing_list";

export type CargoKey =
  | ViewStateKey
  | "exporting"
  | "tempContainer"
  | "container";

const getInitialState = <T>(items: T): ViewState<T> => ({
  items,
  loading: false,
  error: null,
  success: null,
});

const initialState: CargoState = {
  container: {
    success: undefined,
    error: undefined,
  },
  tempContainer: {
    item: null,
    success: undefined,
    error: undefined,
  },
  uploading: {
    loading: false,
    error: null,
    success: null,
  },
  voyage: getInitialState<VoyageInfo[] | null>(null),
  billOfLading: getInitialState<BillLadingCommonProps[] | null>(null),
  exporting: {
    loading: false,
    error: null,
  },
  packing_list: {
    entity: null,
    entity_id: null,
    group_id: null,
    items: null,
    loading: false,
    error: null,
    success: null,
    total: 0,
  },
};

const cargoSlice = createSlice({
  name: "cargo",
  initialState,
  reducers: {
    getStart(state: CargoState, action: PayloadAction<ViewStateKey>) {
      state[action.payload].loading = true;
      state[action.payload].error = null;
    },
    getSuccess(
      state: CargoState,
      action: PayloadAction<{
        key: ViewStateKey;
        items: any;
        total?: number;
        entity_id?: number;
        entity?: string;
        group_id?: number;
      }>,
    ) {
      const { key, items, total, entity_id, entity, group_id } = action.payload;
      state[key].items = items;
      state[key].loading = false;
      state[key].error = null;

      if (key === "packing_list") {
        const packingList = state[key as "packing_list"];
        if (total !== undefined) packingList.total = total;
        if (entity_id !== undefined) packingList.entity_id = entity_id;
        if (entity !== undefined) packingList.entity = entity;
        if (group_id !== undefined) packingList.group_id = group_id;
      }
    },
    createSuccess(
      state: CargoState,
      action: PayloadAction<{ key: ViewStateKey; success: string }>,
    ) {
      const { key, success } = action.payload;
      state[key].success = success;
      state[key].loading = false;
      state[key].error = null;
    },
    getFailure(
      state: CargoState,
      action: PayloadAction<{ key: ViewStateKey; error: string }>,
    ) {
      const { key, error } = action.payload;
      state.exporting.loading = false;
      state[key].loading = false;
      state.uploading.loading = false;
      state[key].error = error;
    },
    cleanData(state: CargoState, action: PayloadAction<ViewStateKey>) {
      state[action.payload].items = null;
    },
    cleanState(state: CargoState, action: PayloadAction<ViewStateKey>) {
      const key = action.payload;
      const currentState = state[key];

      currentState.items = null;
      currentState.loading = false;
      currentState.error = null;
      currentState.success = null;

      if (key === "packing_list") {
        const packingList = state.packing_list;
        packingList.entity_id = null;
        packingList.entity = null;
        packingList.total = 0;
      }
    },
    hideWarning(state: CargoState, action: PayloadAction<ViewStateKey>) {
      const key = action.payload;
      state[key].error = null;
      state[key].success = null;
      state.uploading.error = null;
      state.uploading.success = null;
    },
    getFileUploadingStart(state: CargoState) {
      state.uploading.error = null;
      state.uploading.success = null;
      state.uploading.loading = true;
    },
    getFileUploadingSuccess(state: CargoState, action: PayloadAction<string>) {
      state.uploading.success = action.payload;
      state.uploading.loading = false;
    },
    setCommodities(
      state: CargoState,
      action: PayloadAction<{
        groupId: number;
        type: "set" | "update" | "delete";
        commodities?: CommoditiesProps[];
        id?: number;
      }>,
    ) {
      const { groupId, commodities, type } = action.payload;

      const group = state.billOfLading.items?.find(
        (item) => item.id === groupId,
      );
      if (!group) return;

      switch (type) {
        case "delete": {
          if (!group.commodities) return;
          group.commodities = group.commodities.filter(
            (c) => c.id !== action.payload.id,
          );
          break;
        }
        case "set": {
          group.commodities = commodities as CommoditiesProps[];
          break;
        }

        case "update": {
          if (!group.commodities)
            group.commodities = commodities as CommoditiesProps[];
          const existingIds = new Set(group.commodities.map((c) => c.id));
          const updatedList = group.commodities.map((existing) => {
            const updated = commodities?.find((c) => c.id === existing.id);
            return updated ? { ...existing, ...updated } : existing;
          });

          const newCommodities = commodities?.filter(
            (c) => !existingIds.has(c.id),
          );
          if (newCommodities)
            group.commodities = [...updatedList, ...newCommodities];
          break;
        }

        default:
          break;
      }
    },
    setContainers(
      state: CargoState,
      action: PayloadAction<{
        type: "set" | "update" | "delete" | "error";
        containers?: MaritimeEquipment[];
        groupId: number;
        id?: number;
        error?: string;
        success?: string;
      }>,
    ) {
      const { groupId, containers, type, id, success, error } = action.payload;

      const group = state.billOfLading.items?.find((item) =>
        item.commodities?.some((commodity) => commodity.id === groupId),
      );
      if (!group) return;
      const commodity = group.commodities?.find((c) => c.id === groupId);

      if (!commodity) return;

      switch (type) {
        case "set": {
          state.container.success = success;
          commodity.containers = containers as MaritimeEquipment[];
          break;
        }
        case "delete": {
          if (commodity.containers) {
            const idx = commodity.containers.findIndex((c) => c.id === id);
            if (idx !== -1) {
              commodity.containers.splice(idx, 1);
            }
          }
          break;
        }
        case "error": {
          state.container.success = undefined;
          state.container.error = error;
          break;
        }
        case "update": {
          const updatedContainer = containers?.[0];
          if (!updatedContainer) return;
          if (!commodity.containers) commodity.containers = [];
          const idx = commodity.containers.findIndex(
            (c) => c.id === updatedContainer.id,
          );
          if (idx !== -1) {
            commodity.containers[idx] = {
              ...commodity.containers[idx],
              ...updatedContainer,
            };
          } else {
            commodity.containers.push(updatedContainer);
          }
          state.container.success = success;
          break;
        }

        default:
          break;
      }
    },
    setBilling(
      state: CargoState,
      action: PayloadAction<{
        data: BillLadingCommonProps;
      }>,
    ) {
      const { data } = action.payload;
      if (!state.billOfLading.items) {
        state.billOfLading.items = [{ ...data }];
        return;
      }

      const items = state.billOfLading.items;
      const idx = data.id ? items.findIndex((item) => item.id === data.id) : -1;

      if (idx !== -1) {
        items[idx] = {
          ...items[idx],
          ...data,
        };
      } else {
        items.push({ ...data });
      }
    },
    setBillRemove(state: CargoState, action: PayloadAction<number>) {
      if (!state.billOfLading?.items) return;
      state.billOfLading.items = state.billOfLading.items?.filter(
        (item) => item.id !== action.payload,
      );
    },
    setTempContainer(
      state: CargoState,
      action: PayloadAction<MaritimeEquipment>,
    ) {
      state.tempContainer.item = action.payload;
      state.tempContainer.success = undefined;
      state.tempContainer.error = undefined;
    },
    cleanTempContainer(state: CargoState) {
      state.tempContainer.item = null;
      state.tempContainer.success = undefined;
      state.tempContainer.error = undefined;
    },
    cleanContainer(state: CargoState) {
      state.container.success = undefined;
      state.container.error = undefined;
    },
    startExport(state: CargoState) {
      state.exporting = {
        loading: true,
        error: null,
      };
    },
    successExport(state: CargoState) {
      state.exporting.loading = false;
      state.exporting.error = null;
    },
  },
});

export const {
  getStart,
  getSuccess,
  getFailure,
  cleanData,
  createSuccess,
  hideWarning,
  setCommodities,
  setContainers,
  setBilling,
  setBillRemove,
  cleanContainer,
  getFileUploadingStart,
  getFileUploadingSuccess,
  startExport,
  successExport,
  cleanState,
  setTempContainer,
  cleanTempContainer,
} = cargoSlice.actions;

export const { actions, reducer } = cargoSlice;
export default cargoSlice.reducer;

const selectCargoView = (state: RootState) => state.viewShipping.cargo;

export const selectBillOfLading = createSelector(
  [selectCargoView],
  (cargo) => cargo.billOfLading,
);

export const tempContainer = createSelector(
  [selectCargoView],
  (cargo) => cargo.tempContainer.item,
);

export const bills = createSelector(
  [selectCargoView],
  (shippingView) => shippingView.billOfLading.items || [],
);

export const billsWithSharedMeta = createSelector([bills], (bills) => {
  const countByCommodityId = new Map<number, number>();
  const parentMetaByCommodityId = new Map<
    number,
    { commodityId: number; billId: number }
  >();

  bills.forEach((bill) => {
    bill.commodities.forEach((c) => {
      const commodityId = c.commodity?.id;
      if (!commodityId) return;

      countByCommodityId.set(
        commodityId,
        (countByCommodityId.get(commodityId) ?? 0) + 1,
      );
    });
  });

  return bills.map((bill) => ({
    ...bill,
    commodities: bill.commodities.map<CommodityWithSharedUI>((c) => {
      const commodityId = c.commodity?.id;

      const sharedCount =
        commodityId !== undefined
          ? (countByCommodityId.get(commodityId) ?? 1)
          : 1;

      if (!c.share_general_info || commodityId === undefined) {
        return {
          ...c,
          sharedCount,
        };
      }

      const existingParent = parentMetaByCommodityId.get(commodityId);

      if (!existingParent) {
        parentMetaByCommodityId.set(commodityId, {
          commodityId: c.id,
          billId: bill.id,
        });

        return {
          ...c,
          sharedCount,
          sharedColor: getSharedColorByCommodityId(commodityId),
          sharedParent: true,
          sharedParentId: c.id,
          sharedParentBillId: bill.id,
        };
      }

      return {
        ...c,
        sharedCount,
        sharedColor: getSharedColorByCommodityId(commodityId),
        sharedParent: false,
        sharedParentId: existingParent.commodityId,
        sharedParentBillId: existingParent.billId,
      };
    }),
  }));
});

export const cargoCommody = createSelector([billsWithSharedMeta], (bills) => {
  return bills.flatMap((bill) => {
    if (!bill.commodities || bill.commodities.length === 0) return [];
    return bill.commodities.map((commodity) => ({
      ...commodity,
      mbl_number: bill.mbl_number || null,
    }));
  });
});

export const selectContainersByCommodityId = (commodityId: number) =>
  createSelector([selectBillOfLading], (billOfLading) => {
    for (const item of billOfLading.items ?? []) {
      for (const commodity of item.commodities ?? []) {
        if (commodity.id === commodityId) {
          return commodity.containers ?? [];
        }
      }
    }
    return [];
  });

export const getInitialVoyageData = (
  shipping: ShippingCaseResponse | null | undefined,
): CreateVoyage & {
  port_of_loading?: { id: number; title: string };
  port_of_discharge?: { id: number; title: string };
} => {
  const initial: CreateVoyage & {
    port_of_loading?: { id: number; title: string };
    port_of_discharge?: { id: number; title: string };
  } = { ...VoyageInitialState };

  if (shipping?.offer?.loading_port?.id) {
    initial.port_of_loading_id = shipping.offer.loading_port.id;
    initial.port_of_loading = {
      id: shipping.offer.loading_port.id,
      title: shipping.offer.loading_port.title,
    };
  }

  if (shipping?.offer?.discharge_port?.id) {
    initial.port_of_discharge_id = shipping.offer.discharge_port.id;
    initial.port_of_discharge = {
      id: shipping.offer.discharge_port.id,
      title: shipping.offer.discharge_port.title,
    };
  }

  return initial;
};

export const fetchCommodities =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      const { items } = await getCommoditiesApi(id);
      dispatch(
        setCommodities({ groupId: id, type: "set", commodities: items }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const fetchBillLading =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("billOfLading"));
      const { items } = await fetchBillLadingApi(id);
      dispatch(getSuccess({ key: "billOfLading", items }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const fetchBillLadingItem =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      const data = await fetchBillLadingItemApi(id);
      dispatch(setBilling({ data }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const createBillLading =
  (id: number, params: BillLadingCreate): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      const data = await createBillLadingApi(id, params);
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Bill of Lading created successfully",
        }),
      );
      dispatch(setBilling({ data }));
      return true;
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
      return false;
    }
  };

export const updateBillLading =
  (id: number, params: BillLadingCreate): AppThunk<Promise<boolean>> =>
  async (dispatch) => {
    try {
      await updateBillLadingApi(id, params);
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Bill of Lading updated successfully",
        }),
      );
      dispatch(fetchBillLadingItem(id));
      return true;
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
      return false;
    }
  };

export const removeBill =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeBillApi(id);
      dispatch(setBillRemove(id));
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Bill of Lading removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const createCommodity =
  (params: CreateCommoditiesFields, id: number): AppThunk =>
  async (dispatch) => {
    try {
      const item = await createCommodityApi(params, id);
      dispatch(
        setCommodities({ groupId: id, type: "update", commodities: [item] }),
      );
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Commodity created successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const updateCommodity =
  (
    params: CreateCommoditiesFields,
    id: number,
    editId: number,
    sid: number,
  ): AppThunk =>
  async (dispatch) => {
    try {
      const item = await updateCommodityApi(params, editId);

      if (params.share_general_info) {
        dispatch(fetchBillLading(sid));
      } else {
        dispatch(
          setCommodities({ groupId: id, type: "update", commodities: [item] }),
        );
      }
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Commodity updated successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const removeCommodity =
  (groupId: number, id: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeCommodityApi(id);
      dispatch(setCommodities({ groupId, type: "delete", id }));
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Commodity removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const fetchContainers =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      const { items } = await getContainersApi(id);
      dispatch(setContainers({ groupId: id, type: "set", containers: items }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const getContainerById =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      const item = await getContainerByIdApi(id);
      dispatch(setTempContainer(item));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const createContainer =
  (params: Partial<MaritimeEquipment>, id: number): AppThunk =>
  async (dispatch) => {
    try {
      const item = await createContainerApi(params, id);
      dispatch(
        setContainers({
          type: "update",
          containers: [item],
          groupId: id,
          success: "Container created successfully",
        }),
      );
      dispatch(cleanTempContainer());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(setContainers({ groupId: id, type: "error", error }));
    }
  };

export const updateContainer =
  (params: MaritimeEquipment, groupId: number, id: number): AppThunk =>
  async (dispatch) => {
    try {
      const item = await updateContainerApi(params, id);
      dispatch(
        setContainers({
          type: "update",
          containers: [item],
          groupId,
          id,
          success: "Container updated successfully",
        }),
      );
      dispatch(cleanTempContainer());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(setContainers({ groupId, id, type: "error", error }));
    }
  };

export const removeContainer =
  (id: number, groupId: number): AppThunk =>
  async (dispatch) => {
    try {
      await removeContainerApi(id);
      dispatch(setContainers({ type: "delete", groupId, id }));
      dispatch(
        createSuccess({
          key: "billOfLading",
          success: "Container removed successfully",
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const fetchVoyage =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("voyage"));
      const { items } = await getVoyageApi(id);
      dispatch(getSuccess({ key: "voyage", items }));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const createVoyage =
  (id: number, params: Partial<CreateVoyage>): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getStart("voyage"));
      await createVoyageApi(id, params);
      dispatch(fetchVoyage(id));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const updateVoyage =
  (id: number, voyageId: number, params: Partial<CreateVoyage>): AppThunk =>
  async (dispatch) => {
    try {
      await updateVoyageApi(id, voyageId, params);
      dispatch(fetchVoyage(id));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "billOfLading", error }));
    }
  };

export const exportCargoDetails =
  (id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(startExport());
      const { file, filename } = await exportCargoDetailsApi(id);

      const url = window.URL.createObjectURL(file);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(successExport());
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(
        getFailure({
          key: "billOfLading",
          error,
        }),
      );
    }
  };

export const preloadFileByType =
  (data: File, id: number): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getFileUploadingStart());
      await loadFileApi(data, id);
      dispatch(fetchBillLading(id));
      dispatch(getFileUploadingSuccess("File uploaded successfully"));
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(
        getFailure({
          key: "billOfLading",
          error,
        }),
      );
    }
  };

export const fetchPackingList =
  (params: {
    entity_id: number;
    entity: string;
    page: number;
    size: number;
    group_id?: number;
  }): AppThunk =>
  async (dispatch) => {
    try {
      const { group_id, ...restParams } = params;
      dispatch(getStart("packing_list"));
      const data = await fetchPackingListApi(restParams);
      dispatch(
        getSuccess({
          key: "packing_list",
          items: data.items,
          entity: params.entity,
          entity_id: params.entity_id,
          group_id: params.group_id,
          total: data.total,
        }),
      );
    } catch (err) {
      const error = getErrorMessageFromResponse(err);
      dispatch(getFailure({ key: "packing_list", error }));
    }
  };
